const TOMBSTONE_TTL_MS = 30_000;

export const filterKeyOf = (filter) => [
    filter?.router_id ?? 'router',
    filter?.rule_id ?? 'rule',
    filter?.filter_id ?? filter?.id ?? filter?.pattern ?? 'filter',
].join('|');

export const compareActiveFiltersStable = (a, b) => {
    const routerCmp = String(a?.router_id ?? '').localeCompare(String(b?.router_id ?? ''));
    if (routerCmp !== 0) return routerCmp;

    const ruleCmp = String(a?.rule_id ?? '').localeCompare(String(b?.rule_id ?? ''));
    if (ruleCmp !== 0) return ruleCmp;

    return Number(a?.filter_id ?? a?.id ?? 0) - Number(b?.filter_id ?? b?.id ?? 0);
};

const cloneTombstones = (tombstones, now) => {
    const next = tombstones instanceof Map ? new Map(tombstones) : new Map();
    for (const [key, expiresAt] of next.entries()) {
        if (Number(expiresAt) <= now) {
            next.delete(key);
        }
    }
    return next;
};

const normalizeFilter = ({ routerId, ruleId, filter, timestamp }) => {
    const filterID = filter?.filter_id ?? filter?.id;
    return {
        ...filter,
        router_id: filter?.router_id ?? routerId,
        rule_id: filter?.rule_id ?? ruleId,
        filter_id: filterID,
        created_at: filter?.created_at ?? filter?.added_at ?? timestamp ?? new Date().toISOString(),
    };
};

const upsertFilter = (filters, filter) => {
    const key = filterKeyOf(filter);
    const index = filters.findIndex((item) => filterKeyOf(item) === key);
    if (index === -1) {
        return [...filters, filter];
    }

    const next = [...filters];
    next[index] = { ...next[index], ...filter };
    return next;
};

// Collapse duplicates by identity so a filter can never render twice; later entries win.
const dedupeFilters = (filters) => {
    const byKey = new Map();
    for (const filter of filters) {
        const key = filterKeyOf(filter);
        const previous = byKey.get(key);
        byKey.set(key, previous ? { ...previous, ...filter } : filter);
    }
    return [...byKey.values()];
};

const extractRuleFilters = (rules, routerId, timestamp) => {
    return (Array.isArray(rules) ? rules : []).flatMap((rule) => {
        const ruleId = rule?.rule_id ?? rule?.id;
        const filters = rule?.filters ?? rule?.active_filters ?? rule?.activeFilters ?? [];
        return (Array.isArray(filters) ? filters : []).map((filter) =>
            normalizeFilter({ routerId, ruleId, filter, timestamp })
        );
    });
};

export const reconcileRestFilters = (currentFilters, restFilters, { routerId, now = Date.now(), tombstones } = {}) => {
    const nextTombstones = cloneTombstones(tombstones, now);
    const scopedFilters = (Array.isArray(restFilters) ? restFilters : [])
        .map((filter) => normalizeFilter({ routerId: filter.router_id ?? routerId, ruleId: filter.rule_id, filter }))
        .filter((filter) => !nextTombstones.has(filterKeyOf(filter)));

    // Without a routerId the response covers every router in scope, so it fully
    // replaces the current list; keeping the old entries would duplicate each filter.
    const otherFilters = routerId
        ? (Array.isArray(currentFilters) ? currentFilters : [])
            .filter((filter) => String(filter.router_id) !== String(routerId))
        : [];

    return {
        filters: dedupeFilters([...otherFilters, ...scopedFilters]).sort(compareActiveFiltersStable),
        tombstones: nextTombstones,
    };
};

export const reconcileStatusFilters = (currentFilters, rules, { routerId, timestamp, now = Date.now(), tombstones } = {}) => {
    const nextTombstones = cloneTombstones(tombstones, now);
    const statusFilters = extractRuleFilters(rules, routerId, timestamp)
        .filter((filter) => !nextTombstones.has(filterKeyOf(filter)));
    const otherFilters = (Array.isArray(currentFilters) ? currentFilters : [])
        .filter((filter) => String(filter.router_id) !== String(routerId));

    return {
        filters: dedupeFilters([...otherFilters, ...statusFilters]).sort(compareActiveFiltersStable),
        tombstones: nextTombstones,
    };
};

export const applyFilterAddEvent = (currentFilters, event, { now = Date.now(), tombstones } = {}) => {
    const nextTombstones = cloneTombstones(tombstones, now);
    const filter = normalizeFilter({
        routerId: event.routerId,
        ruleId: event.ruleId,
        filter: event.filter,
        timestamp: event.timestamp,
    });
    nextTombstones.delete(filterKeyOf(filter));

    return {
        filters: upsertFilter(Array.isArray(currentFilters) ? currentFilters : [], filter).sort(compareActiveFiltersStable),
        tombstones: nextTombstones,
    };
};

export const applyFilterRemoveEvent = (currentFilters, event, { now = Date.now(), tombstones } = {}) => {
    const nextTombstones = cloneTombstones(tombstones, now);
    const removed = normalizeFilter({
        routerId: event.routerId,
        ruleId: event.ruleId,
        filter: { filter_id: event.filterId },
        timestamp: event.timestamp,
    });
    const key = filterKeyOf(removed);
    nextTombstones.set(key, now + TOMBSTONE_TTL_MS);

    return {
        filters: (Array.isArray(currentFilters) ? currentFilters : [])
            .filter((filter) => filterKeyOf(filter) !== key)
            .sort(compareActiveFiltersStable),
        tombstones: nextTombstones,
    };
};
